// Zod contract for the owner's monthly social media report (`{salon}.hayven.salon/social/{token}`, its PDF and the
// staff preview): one payload, built by the API's social report builder. The landing page parses exactly what the API
// sends. Aggregates only: no person names, phones or comment text, ever.
import { z } from 'zod';

/** "YYYY-MM", a salon-local calendar month. */
export const SOCIAL_REPORT_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
/** A report link token: 32 random bytes, base64url (43 chars). Anything else can never be a link. */
export const SOCIAL_REPORT_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const socialReportMonthSchema = z.string().regex(SOCIAL_REPORT_MONTH_PATTERN);
export type SocialReportMonth = z.infer<typeof socialReportMonthSchema>;

/** Where new clients found the salon: an Instagram / Facebook post (Comment → DM), or a texted keyword. */
export const SOCIAL_REPORT_SOURCES = ['INSTAGRAM', 'FACEBOOK', 'TEXT_KEYWORDS'] as const;
export const socialReportSourceSchema = z.enum(SOCIAL_REPORT_SOURCES);
export type SocialReportSource = z.infer<typeof socialReportSourceSchema>;

export const socialReportPostKindSchema = z.enum(['VIDEO', 'CAROUSEL', 'PHOTO', 'UNKNOWN']);
export type SocialReportPostKind = z.infer<typeof socialReportPostKindSchema>;

/** The salon setup facts the report's wording reads (a narrow `SalonSetup`). */
export const socialReportSetupSchema = z.object({
  bookingsInHayven: z.boolean(),
  visitsProvedBy: z.enum(['SALE', 'CHECK_IN', 'COUNTER']),
  money: z.enum(['SALES', 'MENU_ESTIMATE', 'TURNS_ESTIMATE', 'NONE']),
  modules: z.object({ turns: z.boolean() }),
});
export type SocialReportSetup = z.infer<typeof socialReportSetupSchema>;

const count = z.number().int().nonnegative();

export const socialReportTotalsSchema = z.object({
  /** Distinct new people across Comment → DM and Text Keywords (a person who did both counts once). */
  newSignups: count,
  cameIn: count,
  /** Null where the bookings are not in Hayven. */
  booked: count.nullable(),
  /** Null where the setup shows no money; each first visit counted once. */
  earnedCents: count.nullable(),
});
export type SocialReportTotals = z.infer<typeof socialReportTotalsSchema>;

export const socialReportSourceLineSchema = z.object({
  source: socialReportSourceSchema,
  /** New people credited here (the earliest comment / text decides), so the lines add up to the total. */
  newSignups: count,
  cameIn: count,
});

export const socialReportTopPostSchema = z.object({
  caption: z.string(),
  platform: z.enum(['INSTAGRAM', 'FACEBOOK']),
  kind: socialReportPostKindSchema,
  /** Salon-local `YYYY-MM-DD` the post went up; null when Meta never said. */
  postedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  /** Our own stored copy (R2) or null: never a Meta CDN link that expires. */
  thumbnailUrl: z.string().url().nullable(),
  /** The post's own Comment → DM numbers for the month (same as the Comment → DM page). */
  newSignups: count,
  cameIn: count,
});
export type SocialReportTopPost = z.infer<typeof socialReportTopPostSchema>;

export const socialReportSchema = z.object({
  salon: z.object({
    name: z.string(),
    logoUrl: z.string().nullable(),
    brandColor: z.string().nullable(),
    /** The address label the landing app matches against the request host; null without a web address. */
    subdomain: z.string().nullable(),
    canonicalOrigin: z.string().nullable(),
  }),
  month: socialReportMonthSchema,
  /** Months the report can show (since the salon's first comment or keyword text), newest first. */
  months: z.array(socialReportMonthSchema),
  /** The month is still running. */
  inProgress: z.boolean(),
  /** Came in can still go up: some sign-up of the month is inside its window. */
  stillCounting: z.boolean(),
  windowDays: z.number().int().positive(),
  setup: socialReportSetupSchema,
  /** Came in is proven only at the counter: the number is the least that came. */
  cameInAtLeast: z.boolean(),
  totals: socialReportTotalsSchema,
  /** The month before, for the "more than September" line; null before the first month with data. */
  previous: z.object({ month: socialReportMonthSchema, newSignups: count, cameIn: count }).nullable(),
  sources: z.array(socialReportSourceLineSchema),
  /** Up to 3 posts by new sign-ups (ties: came in), only posts that brought someone. */
  topPosts: z.array(socialReportTopPostSchema),
  howItWorks: z.object({
    comments: count,
    commenters: count,
    posts: count,
    dmsSent: count,
    /** Comment → DM's own new sign-ups (equals Reports › Marketing for the month). */
    commentDmNewSignups: count,
    /** Text Keywords' own new sign-ups. */
    textKeywordNewSignups: count,
  }),
});
export type SocialReport = z.infer<typeof socialReportSchema>;
